import { useEffect, useRef, useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';

interface Capability {
  configured: boolean;
  modelId: string | null;
  promptVersion: string;
  tools: { name: string; description: string }[];
}

interface Query { tool: string; sql: string; rowCount: number; note?: string }

interface Turn {
  question: string;
  answer: string | null;
  queries: Query[];
  error: string | null;
}

const SUGGESTIONS = [
  'Is there any performance data yet, and what can we actually measure today?',
  'Review our advert copy — what is weak and why?',
  'Do the adverts match the landing pages they point at?',
  'Assess our keyword and match-type strategy for an MSP targeting 50 miles around Doncaster.',
  'What would you change first to improve return, and what evidence would tell us it worked?',
];

export function Analyst({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data: cap, error: capError } = useApi<Capability>('/api/analyst/capability', getToken);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [openQueries, setOpenQueries] = useState<Record<number, boolean>>({});
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns, busy]);

  async function ask(text: string): Promise<void> {
    const q = text.trim();
    if (q.length === 0 || busy) return;
    setQuestion('');
    setBusy(true);
    setTurns((t) => [...t, { question: q, answer: null, queries: [], error: null }]);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBaseUrl}/api/analyst`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ question: q, sessionId }),
      });
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const body = (await res.json()) as {
        sessionId?: number; answer?: string; queries?: Query[]; error?: string;
      };
      if (body.sessionId) setSessionId(body.sessionId);
      setTurns((t) => {
        const next = [...t];
        const last = next[next.length - 1]!;
        next[next.length - 1] = body.error
          ? { ...last, error: body.error }
          : { ...last, answer: body.answer ?? '', queries: body.queries ?? [] };
        return next;
      });
    } catch (e) {
      setTurns((t) => {
        const next = [...t];
        const last = next[next.length - 1]!;
        next[next.length - 1] = { ...last, error: e instanceof Error ? e.message : 'Unknown error' };
        return next;
      });
    } finally { setBusy(false); }
  }

  if (capError) return <EmptyState title="Could not reach the analyst" tag="Error">{capError}</EmptyState>;
  if (!cap) return <EmptyState title="Loading…" tag="Please wait">Checking analyst configuration.</EmptyState>;

  if (!cap.configured) {
    return (
      <div className="stack">
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>The analyst is not switched on</h2>
          <p>
            No AI provider is configured, so this screen reports that rather than showing a chat box that
            would fail on first use. Two things are needed: an Anthropic API key stored in Key Vault as{' '}
            <code>anthropic-api-key</code>, and <code>GE_AI_PROVIDER=ANTHROPIC</code> set on the Function App.
          </p>
        </div>
        <div className="panel">
          <h2>What it will be able to answer</h2>
          <p>
            The analyst cannot write its own SQL. It chooses from {cap.tools.length} curated queries, and
            every number in an answer comes back from one of them — so it can refuse a question, but it
            cannot invent a figure.
          </p>
          <table className="data" style={{ marginTop: 14 }}>
            <thead><tr><th>Tool</th><th>What it returns</th></tr></thead>
            <tbody>
              {cap.tools.map((t) => (
                <tr key={t.name}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, whiteSpace: 'nowrap' }}>{t.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      {turns.length === 0 ? (
        <div className="panel">
          <h2>Ask a question of the advertising and CRM data</h2>
          <p>
            Answers are built from {cap.tools.length} curated queries — every figure comes back from the
            database, and the queries behind each answer are shown so you can check them. The analyst will
            tell you when it cannot answer rather than estimating. Advice on copy, structure and strategy is
            marked as judgement, separately from measured facts.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
            {SUGGESTIONS.map((s) => (
              <button key={s} className="btn-ghost" style={{ textAlign: 'left' }} onClick={() => void ask(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {turns.map((t, i) => (
        <div key={i} className="stack">
          <div className="panel" style={{ background: 'var(--bg-hover)' }}>
            <strong style={{ fontSize: 13 }}>{t.question}</strong>
          </div>
          <div className="panel">
            {t.error ? (
              <p style={{ color: 'var(--negative)' }}>{t.error}</p>
            ) : t.answer === null ? (
              <p style={{ color: 'var(--text-faint)' }}>Thinking, and reading the database…</p>
            ) : (
              <>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.65 }}>{t.answer}</div>
                {t.queries.length > 0 ? (
                  <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <button
                      className="btn-ghost"
                      onClick={() => setOpenQueries((o) => ({ ...o, [i]: !o[i] }))}
                    >
                      {openQueries[i] ? 'Hide' : 'Show'} the {t.queries.length} quer
                      {t.queries.length === 1 ? 'y' : 'ies'} behind this answer
                    </button>
                    {openQueries[i] ? (
                      <div style={{ marginTop: 12 }}>
                        {t.queries.map((q, qi) => (
                          <div key={qi} style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                              <span className="pill">{q.tool}</span>{' '}
                              <span style={{ color: 'var(--text-faint)' }}>{q.rowCount} rows</span>
                            </div>
                            {q.note ? (
                              <div style={{ fontSize: 11, color: 'var(--warning)', marginBottom: 4 }}>{q.note}</div>
                            ) : null}
                            <pre style={{
                              fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-faint)',
                              background: 'var(--bg)', padding: 10, borderRadius: 'var(--radius-sm)',
                              overflow: 'auto', margin: 0, border: '1px solid var(--border)',
                            }}>{q.sql.trim()}</pre>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ))}
      <div ref={endRef} />

      <div className="panel">
        <form
          onSubmit={(e) => { e.preventDefault(); void ask(question); }}
          style={{ display: 'flex', gap: 10 }}
        >
          <input
            className="select"
            style={{ flex: 1, fontSize: 13, padding: '10px 12px' }}
            placeholder={busy ? 'Working…' : 'Ask about spend, keywords, copy, landing pages, pipeline…'}
            value={question}
            disabled={busy}
            onChange={(e) => setQuestion(e.target.value)}
          />
          <button className="btn" type="submit" disabled={busy || question.trim().length === 0}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </form>
        <p style={{ marginTop: 10, fontSize: 11, color: 'var(--text-faint)' }}>
          {cap.modelId} · {cap.promptVersion} · every exchange is stored with its queries so any answer can
          be re-derived later.
        </p>
      </div>
    </div>
  );
}
